import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ShieldCheck, Lock } from "lucide-react";
import { Ambience, Mono, Swap, useAudience, AudienceSwitch } from "./NetworkAtoms";
import { WatchExplainerButton } from "./ExplainerVideo";

const RoutingCore = lazy(() => import("./RoutingCore"));

const COPY = {
  introducer: {
    eyebrow: "Private lending network · invitation only",
    eyebrowShort: "Private network · invitation only",
    h1: ["Introduce the deal.", "We run everything ", "after it."],
    lede: "Connexis places your client's funding with the lenders most likely to fund it, chases the ones that go quiet, and pays you on a schedule you can plan around — from a portal built for exactly this.",
    primary: { to: "/introducer/apply", label: "Apply as an introducer" },
    secondary: { to: "/lender/apply", label: "I'm a lender" },
  },
  lender: {
    eyebrow: "Private lending network · invitation only",
    eyebrowShort: "Private network · invitation only",
    h1: ["Deal flow that arrives", "ready to underwrite."],
    lede: "Vetted introducers, structured applications, documents attached, routed to your desk against criteria you control. Not a spray of unqualified enquiries — cases that fit what you actually fund.",
    primary: { to: "/lender/apply", label: "Apply as a lender" },
    secondary: { to: "/introducer/apply", label: "I introduce deals" },
  },
};

const STRIP = ["Auto-routing engine", "Lender fit-scoring", "E-signed agreements", "UK GDPR · audit-logged"];

export default function NetworkHero() {
  const { audience } = useAudience();
  const reduce = useReducedMotion();
  const c = COPY[audience];

  return (
    <section className="relative overflow-hidden border-b border-white/[0.06]" data-testid="network-hero">
      <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-[#0055FF]/[0.14] to-transparent" />
      <Ambience />

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 pt-16 sm:pt-24 pb-14 sm:pb-20">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-center">
          {/* Copy — 5 cols, hard left */}
          <div className="lg:col-span-5">
            <motion.div initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                        className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.04] pl-2.5 pr-3 sm:pr-3.5 h-8 max-w-full">
                <Lock className="size-3 shrink-0 text-[#7da9ff]" />
                <Mono className="whitespace-nowrap text-[9px] tracking-[0.14em] sm:text-[10.5px] sm:tracking-[0.22em]">
                  <span className="sm:hidden">{c.eyebrowShort}</span>
                  <span className="hidden sm:inline">{c.eyebrow}</span>
                </Mono>
              </div>
            </motion.div>

            <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.1 }} className="mt-5">
              <AudienceSwitch />
            </motion.div>

            <Swap k={`h1-${audience}`}>
              <h1 className="mt-6 font-cabinet text-[31px] sm:text-[52px] lg:text-[58px] leading-[1.02] sm:leading-[0.98] tracking-[-0.03em] sm:tracking-[-0.035em] font-semibold text-white">
                {c.h1[0]}<br />
                <span className="text-[#7da9ff]">
                  {c.h1[1]}
                  {c.h1[2] && <><br className="sm:hidden" />{c.h1[2]}</>}
                </span>
              </h1>
              <p className="mt-5 text-[15px] sm:text-[17px] leading-relaxed text-slate-400 max-w-xl">{c.lede}</p>
            </Swap>

            <motion.div initial={reduce ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.15 }}
                        className="mt-8 flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-3">
              <Link to={c.primary.to} data-testid="hero-primary-cta"
                    className="group inline-flex items-center justify-center gap-2 h-12 px-6 rounded-full bg-[#0055FF] hover:bg-[#0044CC] text-white text-[14.5px] font-semibold transition-colors shadow-[0_10px_40px_-12px_rgba(0,85,255,0.8)]">
                {c.primary.label}
                <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5" />
              </Link>
              <Link to={c.secondary.to} data-testid="hero-secondary-cta"
                    className="inline-flex items-center justify-center gap-2 h-12 px-5 rounded-full border border-white/15 hover:border-white/35 text-white text-[14.5px] font-medium transition-colors">
                {c.secondary.label}
              </Link>
            </motion.div>

            <motion.div initial={reduce ? false : { opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.7, delay: 0.28 }} className="mt-4">
              <WatchExplainerButton full testId="hero-watch-explainer" />
            </motion.div>

            <div className="mt-9 flex flex-wrap items-center gap-x-2 gap-y-1.5 text-[12px] text-slate-500">
              <ShieldCheck className="size-3.5 text-emerald-400/80" />
              Already a member?
              <Link to="/introducer/login" className="text-[#7da9ff] hover:text-white transition-colors underline underline-offset-2" data-testid="hero-introducer-signin">Introducer sign in</Link>
              <span className="text-slate-700">·</span>
              <Link to="/lender/login" className="text-[#7da9ff] hover:text-white transition-colors underline underline-offset-2" data-testid="hero-lender-signin">Lender desk</Link>
            </div>
          </div>

          {/* Visual — 7 cols, bleeding right */}
          <div className="lg:col-span-7 relative">
            <div className="relative aspect-square sm:aspect-[4/3] lg:aspect-auto lg:h-[560px] w-full">
              {reduce ? <StaticCore /> : (
                <Suspense fallback={<StaticCore />}>
                  <RoutingCore />
                </Suspense>
              )}
              <div className="pointer-events-none absolute inset-x-0 -bottom-1 hidden sm:flex justify-center">
                <div className="flex items-center gap-2.5 text-[11.5px] text-slate-500">
                  <span className="text-slate-300">One case in</span>
                  <span className="h-px w-5 bg-white/15" />
                  <span>fit-scored against live criteria</span>
                  <span className="h-px w-5 bg-white/15" />
                  <span>routed to the desks that fund it</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Mono strip */}
        <div className="mt-10 sm:mt-14 border-t border-white/[0.08] pt-5 flex flex-wrap items-center gap-x-8 gap-y-3">
          {STRIP.map((s) => (
            <div key={s} className="flex items-center gap-2">
              <span className="size-1 rounded-full bg-[#0055FF]" />
              <Mono className="text-slate-500">{s}</Mono>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/** Reduced-motion / pre-hydration fallback — still handsome, zero JS cost. */
function StaticCore() {
  return (
    <div className="absolute inset-0 grid place-items-center" aria-hidden>
      <div className="relative size-[300px] sm:size-[380px]">
        <div className="absolute inset-0 rounded-full border border-white/10" />
        <div className="absolute inset-[14%] rounded-full border border-[#7da9ff]/25" />
        <div className="absolute inset-[30%] rounded-full border border-[#0055FF]/40" />
        <div className="absolute inset-[43%] rounded-full bg-[#0055FF] blur-[26px] opacity-70" />
        <div className="absolute inset-[47%] rounded-full bg-white" />
        {[0, 51, 102, 153, 204, 255, 306].map((deg) => (
          <span key={deg}
                className="absolute left-1/2 top-1/2 size-2 -ml-1 -mt-1 rounded-full bg-[#7da9ff]"
                style={{ transform: `rotate(${deg}deg) translateX(150px)` }} />
        ))}
      </div>
    </div>
  );
}
